import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';
const FONT = "'Outfit', system-ui, sans-serif";

const BluIcon = ({ d, size = 24, fill = "none", strokeWidth = 2, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ix = {
    globe: ['M12 22A10 10 0 1 0 12 2a10 10 0 0 0 0 20z', 'M2 12h20', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'],
    building: ['M4 22h16', 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18', 'M10 10h4', 'M10 14h4'],
    card: ['M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M1 10h22'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    pulse: ['M22 12h-4l-3 9L9 3l-3 9H2'],
    check: 'M20 6L9 17l-5-5',
    plus: ['M12 5v14', 'M5 12h14']
};

export default function Dashboard({ user }) {
    const [operators, setOperators] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [opsRes, subsRes] = await Promise.all([
                    axios.get(`${API}/operators`),
                    axios.get(`${API}/subscriptions`)
                ]);
                setOperators(opsRes.data || []);
                setSubscriptions(subsRes.data || []);
            } catch (err) {
                console.error("Dashboard fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    const activeOperators = operators.filter(o => o.status !== 'Inactive').length;
    const totalRevenue = subscriptions.reduce((acc, curr) => acc + (curr.planId?.price || 0), 0);
    const activeSubs = subscriptions.filter(s => s.status === 'Active').length;

    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    const expiringSoon = subscriptions.filter(s => s.endDate && new Date(s.endDate) > now && new Date(s.endDate) <= thirtyDaysFromNow).length;

    const countryMap = {};
    operators.forEach(op => {
        const c = op.country || 'International';
        countryMap[c] = (countryMap[c] || 0) + 1;
    });

    const planMap = {};
    subscriptions.forEach(s => {
        const planName = s.planId?.name || 'Standard';
        planMap[planName] = (planMap[planName] || 0) + 1;
    });
    const planData = Object.keys(planMap).map((k) => ({
        label: k,
        value: planMap[k]
    })).sort((a, b) => b.value - a.value).slice(0, 5);
    const maxPlan = Math.max(...planData.map(d => d.value), 1);
    const totalSubs = subscriptions.length;

    const recentOperators = [...operators].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);

    const Skeleton = () => (
        <div style={{ background: 'linear-gradient(90deg,#eff6ff 25%,#e0e7ff 50%,#eff6ff 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', borderRadius: 8, height: '100%', width: '100%' }} />
    );

    return (
        <div style={{ background: '#f4f7fb', minHeight: '100vh', padding: '40px', fontFamily: FONT, boxSizing: 'border-box', overflowY: 'auto' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes floatUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
                .blu-card { background: white; border-radius: 24px; box-shadow: 0 12px 30px -10px rgba(37,99,235,0.06); border: 1px solid rgba(59,130,246,0.1); padding: 32px; animation: floatUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
                .blu-row { transition: all 0.2s ease; border-radius: 16px; border: 1px solid transparent; }
                .blu-row:hover { background: #f8fafc; border-color: #e2e8f0; transform: translateX(4px); }
            `}</style>

            {/* HERO MEGA HEADER */}
            <div style={{ background: 'linear-gradient(145deg, #1e3a8a 0%, #2563eb 100%)', borderRadius: 32, padding: '48px 56px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 24px 50px -12px rgba(37,99,235,0.4)', marginBottom: 50 }}>
                {/* Abstract overlay graphics */}
                <div style={{ position: 'absolute', top: -150, right: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)' }} />
                <div style={{ position: 'absolute', bottom: -100, left: 100, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(96,165,250,0.3) 0%, rgba(96,165,250,0) 70%)' }} />

                <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 3, color: '#93c5fd', fontWeight: 700, marginBottom: 12 }}>Central Intelligence</div>
                        <div style={{ fontSize: 44, fontWeight: 900, margin: '0 0 12px 0', letterSpacing: '-0.03em', lineHeight: 1.1 }}>Master System<br />Architecture</div>
                        <div style={{ fontSize: 16, color: '#bfdbfe', maxWidth: 450, lineHeight: 1.5, fontWeight: 400 }}>
                            Absolute oversight across the global platform hierarchy, tracking financial growth and tenant ecosystem health.
                        </div>
                    </div>

                    <div style={{ textAlign: 'right', background: 'rgba(255,255,255,0.1)', padding: '32px 40px', borderRadius: 24, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                        <div style={{ fontSize: 13, color: '#dbeafe', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Combined Network Revenue</div>
                        <div style={{ fontSize: 58, fontWeight: 900, letterSpacing: '-0.04em', color: 'white', display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                            <span style={{ color: '#93c5fd' }}>$</span>{loading ? '---' : totalRevenue.toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* Overlapping KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, position: 'absolute', bottom: -120, left: 56, right: 56, height: 140, zIndex: 2 }}>

                </div>
            </div>

            {/* Shift content up to overlay */}
            <div style={{ marginTop: -150, zIndex: 10, position: 'relative', padding: '0 40px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginBottom: 40 }}>
                {[
                    { title: "Network Operators", value: loading ? '-' : activeOperators, icon: ix.building, shade: '#2563eb' },
                    { title: "Live Subscriptions", value: loading ? '-' : activeSubs, icon: ix.card, shade: '#3b82f6' },
                    { title: "Upcoming Renewals", value: loading ? '-' : expiringSoon, icon: ix.shield, shade: '#60a5fa' },
                    { title: "Geographic Reach", value: loading ? '-' : Object.keys(countryMap).length, icon: ix.globe, shade: '#93c5fd' },
                ].map((k, i) => (
                    <div key={i} style={{ background: 'white', borderRadius: 24, padding: '28px', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)', border: '1px solid rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', gap: 24, animation: `floatUp 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1 + 0.2}s both` }}>
                        <div style={{ width: 64, height: 64, borderRadius: 20, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: k.shade, flexShrink: 0 }}>
                            <BluIcon d={k.icon} size={28} strokeWidth={2.5} />
                        </div>
                        <div>
                            <div style={{ fontSize: 32, fontWeight: 900, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.02em' }}>{k.value}</div>
                            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 6 }}>{k.title}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Content Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 32, padding: '0 10px' }}>

                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

                    {/* Chart Replacement - Platform Trajectory */}
                    <div className="blu-card" style={{ animationDelay: '0.4s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                            <div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.02em' }}>Platform Trajectory</div>
                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginTop: 4 }}>Simulated adoption curve based on operator engagement</div>
                            </div>
                            <div style={{ background: '#f8fafc', padding: '8px 16px', borderRadius: 99, color: '#3b82f6', fontSize: 12, fontWeight: 700 }}>
                                Trailing 12 Months
                            </div>
                        </div>

                        <div style={{ height: 260, position: 'relative', background: '#f8fafc', borderRadius: 20, padding: '0 0px', overflow: 'hidden' }}>
                            {/* Decorative Line Chart */}
                            <svg viewBox="0 0 800 200" style={{ width: '100%', height: '100%', display: 'block' }} preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="blueG" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.01" />
                                    </linearGradient>
                                </defs>
                                <path d="M0,170 C150,140 250,180 350,120 C450,60 600,120 700,70 C750,40 800,50 800,50 L800,200 L0,200 Z" fill="url(#blueG)" />
                                <path d="M0,170 C150,140 250,180 350,120 C450,60 600,120 700,70 C750,40 800,50 800,50" fill="none" stroke="#2563eb" strokeWidth="4" />

                                <circle cx="350" cy="120" r="6" fill="#fff" stroke="#2563eb" strokeWidth="3" />
                                <circle cx="700" cy="70" r="6" fill="#fff" stroke="#2563eb" strokeWidth="3" />
                                <circle cx="800" cy="50" r="6" fill="#fff" stroke="#2563eb" strokeWidth="3" />
                            </svg>

                            {/* Axis Mock */}
                            <div style={{ position: 'absolute', bottom: 12, left: 30, right: 30, display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>
                                <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span><span>Jul</span><span>Aug</span><span>Sep</span><span>Oct</span><span>Nov</span><span>Dec</span>
                            </div>
                        </div>
                    </div>

                    {/* Recent Registrations Table */}
                    <div className="blu-card" style={{ animationDelay: '0.5s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                            <div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.02em' }}>Tenant Roster</div>
                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginTop: 4 }}>Recently deployed operator instances</div>
                            </div>
                        </div>

                        <div style={{ background: 'white' }}>
                            {loading ? (
                                <div style={{ height: 200 }}><Skeleton /></div>
                            ) : recentOperators.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>No tenants recorded</div>
                            ) : (
                                recentOperators.map((op, i) => (
                                    <div key={op._id || i} className="blu-row" style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', marginBottom: 8, background: i % 2 === 0 ? '#f8fafc' : 'white' }}>
                                        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, marginRight: 16 }}>
                                            {(op.companyName || 'U').slice(0, 1).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{op.companyName || 'Unnamed Operator'}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                                                {op.primaryEmail || op.email || 'No formal contact'} • {op.country || 'International'}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 800, padding: '6px 14px', borderRadius: 99, background: op.status === 'Active' ? '#eff6ff' : '#f1f5f9', color: op.status === 'Active' ? '#2563eb' : '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {op.status || 'Pending'}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

                    {/* Vertical Progress Bars - Subscriptions by Plan */}
                    <div className="blu-card" style={{ animationDelay: '0.6s' }}>
                        <div style={{ marginBottom: 32 }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.02em' }}>Plan Architecture</div>
                            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginTop: 4 }}>Active licenses categorized by tier</div>
                        </div>

                        {loading ? (
                            <div style={{ height: 200 }}><Skeleton /></div>
                        ) : planData.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>No subscriptions</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                {planData.map((d, i) => {
                                    const pct = maxPlan === 0 ? 0 : (d.value / Math.max(totalSubs, 1)) * 100;
                                    // Use varying shades of blue
                                    const shades = ['#1e3a8a', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'];
                                    const shade = shades[i % shades.length];

                                    return (
                                        <div key={d.label}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>{d.label}</span>
                                                <span style={{ fontSize: 14, fontWeight: 900, color: shade }}>{d.value} <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>({Math.round(pct)}%)</span></span>
                                            </div>
                                            <div style={{ height: 12, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${pct}%`, background: shade, borderRadius: 99, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    {/* Expiry Radar */}
                    <div className="blu-card" style={{ animationDelay: '0.7s', background: 'linear-gradient(135deg, white 0%, #f8fafc 100%)' }}>
                        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <BluIcon d={ix.pulse} size={20} />
                            </div>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.02em' }}>Expiry Radar</div>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Contracts expiring within 30 days</div>
                            </div>
                        </div>

                        {loading ? (
                            <div style={{ height: 100 }}><Skeleton /></div>
                        ) : (
                            <div>
                                {subscriptions.filter(s => s.endDate && new Date(s.endDate) > now && new Date(s.endDate) <= thirtyDaysFromNow).slice(0, 4).map((sub, i) => (
                                    <div key={sub._id || i} style={{ padding: '12px 16px', background: 'white', borderRadius: 12, border: '1px solid #eff6ff', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 4px rgba(37,99,235,0.02)' }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{sub.operatorId?.companyName || 'Operator'}</div>
                                            <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, marginTop: 2 }}>Ends: {new Date(sub.endDate).toLocaleDateString()}</div>
                                        </div>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a' }}>
                                            ${sub.planId?.price || 0}
                                        </div>
                                    </div>
                                ))}
                                {expiringSoon === 0 && (
                                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                                        <div style={{ color: '#2563eb', marginBottom: 8 }}><BluIcon d={ix.check} size={32} /></div>
                                        <div style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>All contracts secure.</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}
